import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import api from '@/api/client';
import { posApi, type PosHardwareConfig } from '@/api/pos.api';
import { useAppSelector } from '@/hooks/useAppSelector';
import { usePosSession } from '@/hooks/usePosSession';
import {
  connectSerialPrinter,
  connectSerialScale,
  disconnectSerialPrinter,
  disconnectSerialScale,
  isBarcodePrinterReady,
  loadLocalHardwarePrefs,
  markBarcodePrinterBrowserReady,
  markReceiptPrinterBrowserReady,
  openCashDrawer,
  printHtmlDocument,
  serialSupported,
  type PosLocalHardwarePrefs,
} from '@/utils/posHardware';

import { notify } from '@/core/toast/notify';
interface PosHardwareContextValue {
  config: PosHardwareConfig | null;
  prefs: PosLocalHardwarePrefs;
  serialSupported: boolean;
  reloadConfig: () => Promise<void>;
  connectScale: () => Promise<void>;
  disconnectScale: () => Promise<void>;
  connectBarcodePrinterBrowser: () => void;
  connectBarcodePrinterSerial: () => Promise<void>;
  disconnectBarcodePrinter: () => Promise<void>;
  connectReceiptPrinterBrowser: () => void;
  connectReceiptPrinterSerial: () => Promise<void>;
  disconnectReceiptPrinter: () => Promise<void>;
  testCashDrawer: () => Promise<void>;
  printBarcodeLabel: (productId: number, copies?: number) => Promise<void>;
  printReceiptHtml: (html: string) => void;
  kickCashDrawerIfEnabled: (paymentMethods: string[]) => Promise<void>;
  barcodePrinterReady: boolean;
}

const PosHardwareCtx = createContext<PosHardwareContextValue | null>(null);

export function usePosHardware() {
  const ctx = useContext(PosHardwareCtx);
  if (!ctx) throw new Error('usePosHardware requires PosHardwareProvider');
  return ctx;
}

export function PosHardwareProvider({ children }: { children: ReactNode }) {
  const store = useAppSelector((s) => s.app.selectedStore);
  const { counterId } = usePosSession();
  const [config, setConfig] = useState<PosHardwareConfig | null>(null);
  const [prefs, setPrefs] = useState<PosLocalHardwarePrefs>(() => loadLocalHardwarePrefs());

  const reloadConfig = useCallback(async () => {
    if (!store?.id) {
      setConfig(null);
      return;
    }
    const hw = await posApi.getHardwareConfig(store.id, counterId);
    setConfig(hw);
  }, [store?.id, counterId]);

  useEffect(() => {
    void reloadConfig().catch(() => setConfig(null));
  }, [reloadConfig]);

  const refreshPrefs = useCallback(() => setPrefs(loadLocalHardwarePrefs()), []);

  const connectScale = useCallback(async () => {
    try {
      const label = await connectSerialScale();
      refreshPrefs();
      notify.success(`Scale connected: ${label}`);
    } catch (err) {
      notify.error(err instanceof Error ? err.message : 'Scale connection failed');
    }
  }, [refreshPrefs]);

  const disconnectScale = useCallback(async () => {
    await disconnectSerialScale();
    refreshPrefs();
    notify.success('Scale disconnected');
  }, [refreshPrefs]);

  const connectBarcodePrinterBrowser = useCallback(() => {
    markBarcodePrinterBrowserReady();
    refreshPrefs();
    notify.success('Label printer ready — use system print dialog');
  }, [refreshPrefs]);

  const connectBarcodePrinterSerial = useCallback(async () => {
    try {
      const label = await connectSerialPrinter('barcode');
      refreshPrefs();
      notify.success(`Label printer connected: ${label}`);
    } catch (err) {
      notify.error(err instanceof Error ? err.message : 'Label printer connection failed');
    }
  }, [refreshPrefs]);

  const disconnectBarcodePrinter = useCallback(async () => {
    await disconnectSerialPrinter('barcode');
    refreshPrefs();
    notify.success('Label printer disconnected');
  }, [refreshPrefs]);

  const connectReceiptPrinterBrowser = useCallback(() => {
    markReceiptPrinterBrowserReady();
    refreshPrefs();
    notify.success('Receipt printer ready — use system print dialog');
  }, [refreshPrefs]);

  const connectReceiptPrinterSerial = useCallback(async () => {
    try {
      const label = await connectSerialPrinter('receipt');
      refreshPrefs();
      notify.success(`Receipt printer connected: ${label}`);
    } catch (err) {
      notify.error(err instanceof Error ? err.message : 'Receipt printer connection failed');
    }
  }, [refreshPrefs]);

  const disconnectReceiptPrinter = useCallback(async () => {
    await disconnectSerialPrinter('receipt');
    refreshPrefs();
    notify.success('Receipt printer disconnected');
  }, [refreshPrefs]);

  const printBarcodeLabel = useCallback(
    async (productId: number, copies = 1) => {
      if (!store?.id) throw new Error('Select a store first');
      if (!config?.barcodePrinterEnabled) throw new Error('Enable barcode printer in POS Setup → Hardware');
      if (!isBarcodePrinterReady(prefs, !!config.barcodePrinterEnabled)) {
        throw new Error('Connect label printer in POS Setup → Hardware first');
      }
      const response = await api.get<string>('/v1/pos/barcodes/label', {
        params: { productId, storeId: store.id, copies },
        responseType: 'text',
        transformResponse: [(d) => d],
      });
      printHtmlDocument(response.data, `pos-label-${productId}`);
    },
    [config?.barcodePrinterEnabled, prefs, store?.id],
  );

  const printReceiptHtml = useCallback(
    (html: string) => {
      if (config?.thermalPrinterEnabled === false) return;
      printHtmlDocument(html, 'pos-receipt');
    },
    [config?.thermalPrinterEnabled],
  );

  const testCashDrawer = useCallback(async () => {
    try {
      await openCashDrawer();
      notify.success('Cash drawer kick sent');
    } catch (err) {
      notify.error(err instanceof Error ? err.message : 'Cash drawer kick failed');
    }
  }, []);

  const kickCashDrawerIfEnabled = useCallback(
    async (paymentMethods: string[]) => {
      if (!config?.cashDrawerEnabled) return;
      if (!paymentMethods.some((m) => m === 'CASH')) return;
      try {
        await openCashDrawer();
      } catch {
        /* browser-only setups skip drawer kick */
      }
    },
    [config?.cashDrawerEnabled],
  );

  const barcodePrinterReady = isBarcodePrinterReady(prefs, !!config?.barcodePrinterEnabled);

  const value = useMemo<PosHardwareContextValue>(
    () => ({
      config,
      prefs,
      serialSupported: serialSupported(),
      reloadConfig,
      connectScale,
      disconnectScale,
      connectBarcodePrinterBrowser,
      connectBarcodePrinterSerial,
      disconnectBarcodePrinter,
      connectReceiptPrinterBrowser,
      connectReceiptPrinterSerial,
      disconnectReceiptPrinter,
      testCashDrawer,
      printBarcodeLabel,
      printReceiptHtml,
      kickCashDrawerIfEnabled,
      barcodePrinterReady,
    }),
    [
      config,
      prefs,
      reloadConfig,
      connectScale,
      disconnectScale,
      connectBarcodePrinterBrowser,
      connectBarcodePrinterSerial,
      disconnectBarcodePrinter,
      connectReceiptPrinterBrowser,
      connectReceiptPrinterSerial,
      disconnectReceiptPrinter,
      testCashDrawer,
      printBarcodeLabel,
      printReceiptHtml,
      kickCashDrawerIfEnabled,
      barcodePrinterReady,
    ],
  );

  return <PosHardwareCtx.Provider value={value}>{children}</PosHardwareCtx.Provider>;
}
